import React, { useEffect } from 'react';
import CountdownTimer from './CountdownTimer';
import CTAButton from './CTAButton';
import LazyVideo from './LazyVideo';
import { logScrollContainers } from '../utils/findScrollContainers';
import { trackLandingPageVideo } from '../utils/analytics';
import useGTM from '../hooks/useGTM';

const LandingPage: React.FC = () => {
    // Hook de Google Tag Manager
    const { trackCTA, trackVideo, trackScroll, trackAction } = useGTM();

    // Verificador automático de scroll containers (solo en desarrollo)
    useEffect(() => {
        if (process.env.NODE_ENV !== 'production') {
            // Ejecutar después de que el DOM esté completamente cargado
            const timer = setTimeout(() => {
                logScrollContainers();
            }, 1000);
            
            return () => clearTimeout(timer);
        }
    }, []);

    // Funciones de tracking para videos (combinando GA y GTM)
    const handleVideoPlay = (videoName: string, section: string) => {
        // Tracking con Google Analytics (existente)
        trackLandingPageVideo(videoName, section);
        
        // Tracking adicional con Google Tag Manager
        trackVideo('play', videoName, section);
    };

    // Función para trackear clicks en CTAs
    const handleCTAClick = (ctaName: string, section: string) => {
        trackCTA(ctaName, section, 'button');
    };

    // Función para trackear scroll depth
    const handleScrollDepth = (depth: number) => {
        trackScroll(depth);
    };

    // Función para trackear acciones específicas
    const handleUserAction = (action: string, category: string, label: string) => {
        trackAction(action, category, label);
    };

    return (
        <div id="app-root" className="relative flex min-h-[100svh] flex-col bg-yellow-50 w-full overflow-x-clip">
            {/* Countdown Timer */}
            <CountdownTimer />
            
            {/* Header */}
            <header className="bg-yellow-50 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between py-3 sm:py-4">
                        <div className="flex items-center space-x-2 sm:space-x-4">
                            <img 
                                src="/images/princonser kids logo.png" 
                                alt="Princonser Kids Logo" 
                                className="h-10 sm:h-12 md:h-14 w-auto"
                            />
                            <h1 className="hidden sm:block text-sm sm:text-base md:text-lg lg:text-xl font-bold text-gray-900 leading-tight">
                                Primera Experiencia Inmersiva de Educación Integral
                            </h1>
                        </div>
                    </div>
                </div>
            </header>

            <main className="flex-1">
                {/* Hero Section */}
            <section 
                id="inicio" 
                className="relative min-h-[100svh] flex items-center justify-center overflow-hidden"
                style={{
                    background: `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)), url('/images/landingpage/FondoHeroLandingpage.png')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                {/* Contenido visible del hero */}
                <div className="relative z-10 max-w-7xl w-full px-4 sm:px-6 lg:px-8">
                    <div className="text-center text-white">
                        {/* Título principal mejorado */}
                        <div className="mb-6 sm:mb-8">
                            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl xl:text-7xl font-bold mb-4 sm:mb-6 leading-tight drop-shadow-2xl">
                                <span className="text-yellow-300">Primera Experiencia</span><br/>
                                <span className="text-white">Inmersiva de Educación</span><br/>
                                <span className="text-yellow-300">Integral basada en</span><br/>
                                <span className="text-white">Sabiduría</span>
                            </h1>
                            <p className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl text-gray-200 mb-6 sm:mb-8 max-w-4xl mx-auto leading-relaxed drop-shadow-lg px-2">
                                <strong>Transforma el futuro de tus hijos</strong> con la única metodología que combina cuentos clásicos, desarrollo cognitivo y formación financiera
                            </p>
                        </div>

                        {/* Video Section mejorado */}
                        <div className="w-full mb-6 sm:mb-8 max-w-5xl mx-auto">
                            <div className="relative w-full bg-black/80 rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl border-2 sm:border-4 border-yellow-300">
                                <LazyVideo
                                    src="/videos/landingpage/video bruto v.1.mp4"
                                    poster="/images/landingpage/video-poster.jpg"
                                    className="w-full h-auto"
                                    videoId="hero-video"
                                    onPlay={() => handleVideoPlay('Video Bruto v.1', 'Hero Section')}
                                />
                            </div>
                            
                            {/* CTA Button centrado debajo del video */}
                            <div className="mt-4 sm:mt-8 text-center px-4">
                                <CTAButton 
                                    ctaName="Da el primer paso en la formación integral de tus hijos"
                                    section="Hero Section"
                                    as="a"
                                    href="https://www.amazon.com/dp/B0FX2BBP1D?ref_=pe_93986420_774957520"
                                >
                                    Da el primer paso en la formación integral de tus hijos
                                </CTAButton>
                                <p className="text-xs sm:text-sm text-gray-300 mt-2 sm:mt-4">
                                    ✅ Garantía de satisfacción • ✅ Soporte 24/7
                                </p>
                            </div>
                        </div>
                        
                        {/* Endorsements mejorados */}
                        <div className="bg-white/50 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 max-w-4xl mx-auto shadow-lg">
                            <p className="text-xs sm:text-sm text-gray-800 mb-3 sm:mb-4 font-semibold uppercase tracking-wide">Avalado por instituciones reconocidas:</p>
                            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 lg:space-x-8">
                                <div className="flex items-center space-x-2 sm:space-x-3">
                                    <img 
                                        src="/images/ForoFilosofia.png" 
                                        alt="World Philosophical Forum" 
                                        className="h-8 sm:h-10 lg:h-12 w-auto opacity-90 hover:opacity-100 transition-opacity"
                                    />
                                    <div className="text-left">
                                        <div className="text-black font-semibold text-xs sm:text-sm">World Philosophical Forum</div>
                                        <div className="text-gray-600 text-xs">Institución Internacional</div>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-2 sm:space-x-3">
                                    <img 
                                        src="/images/PrinconserUniversity.png" 
                                        alt="Princonser University" 
                                        className="h-8 sm:h-10 lg:h-12 w-auto opacity-90 hover:opacity-100 transition-opacity"
                                    />
                                    <div className="text-left">
                                        <div className="text-black font-semibold text-xs sm:text-sm">Princonser University</div>
                                        <div className="text-gray-600 text-xs">Universidad Certificada</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Jean Piaget Section */}
            <section 
                className="py-12 sm:py-16 lg:py-20 relative"
                style={{
                    background: `linear-gradient(rgba(255,255,255,0.8), rgba(255,255,255,0.8)), url('/images/landingpage/FondoSection2Landingpage.png')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="text-center mb-8 sm:mb-12">
                        <div className="inline-block bg-red-100 text-red-800 px-3 py-2 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4">
                            🧠 FUNDAMENTO CIENTÍFICO
                        </div>
                        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4 sm:mb-6 px-2">
                            ¿Por qué nos enfocamos en niños de 
                            <span className="text-red-600"> educación primaria</span>?
                        </h2>
                    </div>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12">
                        {/* Text Content */}
                        <div className="space-y-4 sm:space-y-6">
                            <div className="bg-white/80 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 shadow-xl border border-red-200">
                                <div className="flex items-center mb-3 sm:mb-4">
                                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-100 rounded-full flex items-center justify-center mr-3 sm:mr-4">
                                        <span className="text-xl sm:text-2xl">🧠</span>
                                    </div>
                                    <h3 className="text-xl sm:text-2xl font-bold text-red-600">Jean Piaget</h3>
                                </div>
                                <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed mb-3 sm:mb-4">
                                    <span className="font-bold text-red-600">Según</span> Jean Piaget (1896-1980), considerado <span className="font-bold text-red-600">el Padre de la Psicología Evolutiva</span>, la etapa en niños de siete a once años es <span className="font-bold text-red-600">la más importante para el desarrollo cognitivo</span>.
                                </p>
                                <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed">
                                    Es cuando aprenden a usar la lógica y a razonar sobre eventos concretos y tangibles.
                                </p>
                            </div>
                            
                            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border-l-4 border-yellow-400">
                                <h4 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 sm:mb-3">🎯 Ventana de Oportunidad</h4>
                                <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed">
                                    Si tienes hijos en Educación Primaria (de 7 a 12 años) permíteles iniciar el viaje hacia la sabiduría en el momento más crítico de su desarrollo.
                                </p>
                            </div>
                            
                            <div className="flex justify-center lg:justify-start">
                                <CTAButton 
                                    ctaName="Quiero aumentar el desarrollo mental de mis hijos"
                                    section="Jean Piaget Section"
                                    as="a"
                                    href="https://www.amazon.com/dp/B0FX2BBP1D?ref_=pe_93986420_774957520"
                                >
                                    Quiero aumentar el desarrollo mental de mis hijos
                                </CTAButton>
                            </div>
                        </div>
                        
                        {/* Jean Piaget Illustration */}
                        <div className="flex justify-center lg:justify-end">
                            <div className="relative">
                                <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-2xl max-w-sm transform hover:scale-105 transition-transform duration-300">
                                    <img 
                                        src="/images/JeanPiaget.png" 
                                        alt="Jean Piaget" 
                                        className="w-full h-auto object-contain mb-3 sm:mb-4"
                                    />
                                    <div className="text-center">
                                        <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-1 sm:mb-2">Jean Piaget</h3>
                                        <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3">Padre de la Psicología Evolutiva</p>
                                        <div className="bg-red-50 rounded-lg p-2 sm:p-3">
                                            <p className="text-xs text-red-700 font-semibold">
                                                "La inteligencia es lo que usas cuando no sabes qué hacer"
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                {/* Efecto decorativo */}
                                <div className="absolute -top-2 -right-2 sm:-top-4 sm:-right-4 w-6 h-6 sm:w-8 sm:h-8 bg-yellow-400 rounded-full flex items-center justify-center">
                                    <span className="text-white text-xs sm:text-sm">✨</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Parents Concern Section */}
            <section 
                className="py-12 sm:py-16 lg:py-20 relative"
                style={{
                    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="text-center mb-8 sm:mb-12">
                        <div className="inline-block bg-orange-100 text-orange-800 px-3 py-2 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4">
                            😰 ESTADÍSTICA ALARMANTE
                        </div>
                        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6 px-2">
                            Primera preocupación de los 
                            <span className="text-yellow-300"> padres de hoy</span>
                        </h2>
                    </div>
                    
                    <div className="flex flex-col lg:flex-row items-center gap-4 sm:gap-6 lg:gap-8">
                        {/* Family Illustration */}
                        <div className="flex-shrink-0 w-full lg:w-auto">
                            <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-3 sm:p-4 lg:p-6 shadow-2xl max-w-xs sm:max-w-sm mx-auto transform hover:scale-105 transition-transform duration-300 w-full">
                                <img 
                                    src="/images/PreocupacionPadres.png" 
                                    alt="Familia preocupada por la salud mental de sus hijos" 
                                    className="w-full h-auto object-contain"
                                />
                            </div>
                        </div>
                        
                        {/* Statistics */}
                        <div className="flex-1 min-w-0 w-full">
                            <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-2xl">
                                <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-2 sm:mb-3 lg:mb-4 drop-shadow-lg">55%</div>
                                <p className="text-xs sm:text-sm lg:text-base text-white leading-relaxed font-semibold">
                                    De los padres en Estados Unidos dicen que su principal preocupación es la 
                                    <span className="text-yellow-300"> Salud Mental de sus hijos</span> 
                                    (ansiedad y depresión).
                                </p>
                            </div>
                        </div>
                        
                        {/* Video Testimonial */}
                        <div className="flex-shrink-0 w-full lg:w-auto">
                            <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-3 sm:p-4 lg:p-6 shadow-2xl border-2 sm:border-4 border-yellow-300 max-w-sm sm:max-w-md lg:max-w-lg mx-auto w-full">
                                <div className="relative w-full bg-black/80 rounded-xl sm:rounded-2xl overflow-hidden shadow-xl">
                                    <LazyVideo
                                        src="/videos/landingpage/Escritorio 1 (v.1).mp4"
                                        poster="/images/landingpage/video-poster.jpg"
                                        className="w-full h-auto"
                                        videoId="parents-video"
                                        onPlay={() => handleVideoPlay('Escritorio 1 (v.1)', 'Parents Concern Section')}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* CTA Button */}
                    <div className="text-center mt-8 sm:mt-12">
                        <CTAButton 
                            ctaName="Quiero que mi hijo inicie hoy el camino hacia la sabiduría"
                            section="Parents Concern Section"
                            as="a"
                            href="https://www.amazon.com/dp/B0FX2BBP1D?ref_=pe_93986420_774957520"
                        >
                            Quiero que mi hijo inicie hoy el camino hacia la sabiduría
                        </CTAButton>
                        <div className="bg-black/60 backdrop-blur-sm rounded-lg px-4 py-2 mt-2 sm:mt-4 inline-block border border-white/20">
                            <p className="text-white text-xs sm:text-sm font-medium">
                                🔒 Tu información está 100% protegida • 📞 Soporte personalizado incluido
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Financial Concern Section */}
            <section 
                className="py-12 sm:py-16 lg:py-20 relative"
                style={{
                    backgroundImage: `linear-gradient(rgba(255,255,255,0.3), rgba(255,255,255,0.3)), url('/images/landingpage/FondoSection3Landingpage.png')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="text-center mb-8 sm:mb-12">
                        <div className="inline-block bg-green-100 text-green-800 px-3 py-2 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4">
                            💰 ESTADÍSTICA FINANCIERA
                        </div>
                        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4 sm:mb-6 px-2">
                            Preocupación financiera de 
                            <span className="text-green-600"> sus hijos</span>
                        </h2>
                    </div>

                    <div className="flex flex-col lg:flex-row items-start gap-4 sm:gap-6 lg:gap-8">
                        {/* Video Caso de Éxito - Left Side */}
                        <div className="flex-shrink-0 w-full lg:w-auto mt-4 sm:mt-6 lg:mt-8">
                            <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl sm:rounded-3xl p-4 sm:p-5 lg:p-7 text-center shadow-2xl border-2 sm:border-4 border-green-300 max-w-md sm:max-w-lg lg:max-w-xl mx-auto w-full">
                                <div className="relative w-full bg-black/80 rounded-xl sm:rounded-2xl overflow-hidden shadow-xl">
                                    <LazyVideo
                                        src="/videos/landingpage/escritorio 2 v.1.mp4"
                                        poster="/images/landingpage/video-poster.jpg"
                                        className="w-full h-auto"
                                        videoId="financial-video"
                                        onPlay={() => handleVideoPlay('escritorio 2 v.1', 'Financial Concern Section')}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Right Side - Statistics and Image */}
                        <div className="flex-1 min-w-0 space-y-4 sm:space-y-6 lg:space-y-8 w-full">
                            {/* Statistics */}
                            <div className="bg-gradient-to-r from-green-500 to-blue-500 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-2xl">
                                <div className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-2 sm:mb-3 lg:mb-4 drop-shadow-lg">9 de 10</div>
                                <p className="text-xs sm:text-sm lg:text-base text-white leading-relaxed font-semibold">
                                    padres dicen que es <span className="text-yellow-300">'extremadamente'</span> importante o <span className="text-yellow-300">'muy'</span> importante que sus hijos sean financieramente independientes.
                                </p>
                            </div>

                            {/* Family Illustration */}
                            <div className="flex justify-center lg:justify-start">
                                <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-3 sm:p-4 lg:p-6 shadow-2xl max-w-xs sm:max-w-sm mx-auto transform hover:scale-105 transition-transform duration-300 w-full">
                                    <img
                                        src="/images/FuturoEconomico.png"
                                        alt="Padres preocupados por el futuro económico de sus hijos"
                                        className="w-full h-auto object-contain"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* CTA Button */}
                    <div className="text-center mt-8 sm:mt-12">
                        <CTAButton 
                            ctaName="Quiero que mis hijos tengan un futuro prometedor"
                            section="Financial Concern Section"
                            as="a"
                            href="https://www.amazon.com/dp/B0FX2BBP1D?ref_=pe_93986420_774957520"
                        >
                            Quiero que mis hijos tengan un futuro prometedor
                        </CTAButton>
                        <div className="mt-2 sm:mt-4">
                            <div className="bg-black/60 backdrop-blur-sm rounded-lg px-4 py-2 inline-block border border-white/20">
                                <p className="text-white text-xs sm:text-sm font-medium">
                                    🎯 Formación financiera práctica • 💡 Proyectos reales • 🏆 Certificación incluida
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Purpose Importance Section */}
            <section 
                className="py-12 sm:py-16 lg:py-20 relative"
                style={{
                    background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="text-center mb-8 sm:mb-12">
                        <div className="inline-block bg-purple-100 text-purple-800 px-3 py-2 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4">
                            🎯 FUNDAMENTO CIENTÍFICO
                        </div>
                        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6 px-2">
                            Importancia de un 
                            <span className="text-purple-300"> propósito</span>
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
                        {/* Left Column - Illustration */}
                        <div className="flex justify-center lg:justify-start">
                            <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-2 sm:p-3 lg:p-4 shadow-2xl max-w-xs sm:max-w-sm transform hover:scale-105 transition-transform duration-300 w-full">
                                <img
                                    src="/images/MultiplesEstudios.png"
                                    alt="Niño apuntando con propósito"
                                    className="w-full h-auto object-contain"
                                />
                            </div>
                        </div>

                        {/* Right Column - Text Content and Video */}
                        <div className="space-y-4 sm:space-y-6">
                            {/* Text Content */}
                            <div className="bg-white/90 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-5 lg:p-6 border border-purple-300 w-full shadow-lg">
                                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800 mb-3 sm:mb-4 text-center lg:text-left">
                                    Múltiples estudios
                                </h3>
                                <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed text-center lg:text-left">
                                    concluyen que tener un propósito ayuda a regular el estrés, y mejora significativamente la <span className="text-purple-600 font-bold">Salud Mental</span>.
                                </p>
                            </div>

                            {/* Video */}
                            <div className="flex justify-center lg:justify-start">
                                <div className="w-full max-w-sm sm:max-w-md lg:max-w-lg">
                                    <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-3 sm:p-4 lg:p-5 shadow-2xl border-2 sm:border-4 border-purple-300">
                                        <div className="relative w-full bg-black/80 rounded-xl sm:rounded-2xl overflow-hidden shadow-xl">
                                            <LazyVideo
                                                src="/videos/landingpage/escritorio 3 v.2.mp4"
                                                poster="/images/landingpage/video-poster.jpg"
                                                className="w-full h-auto"
                                                videoId="purpose-video"
                                                onPlay={() => handleVideoPlay('escritorio 3 v.2', 'Purpose Importance Section')}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* CTA Button */}
                    <div className="text-center mt-8 sm:mt-12">
                        <CTAButton 
                            ctaName="Quiero que mis hijos adquieran propósito"
                            section="Purpose Importance Section"
                            as="a"
                            href="https://www.amazon.com/dp/B0FX2BBP1D?ref_=pe_93986420_774957520"
                        >
                            Quiero que mis hijos adquieran propósito
                        </CTAButton>
                        <div className="mt-2 sm:mt-4">
                            <div className="bg-black/60 backdrop-blur-sm rounded-lg px-4 py-2 inline-block border border-white/20">
                                <p className="text-white text-xs sm:text-sm font-medium">
                                    📚 Metodología probada • 🧠 Desarrollo cognitivo • 💪 Fortaleza mental
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Our Goal is Clear Section */}
            <section 
                className="py-12 sm:py-16 lg:py-20 relative"
                style={{
                    background: `linear-gradient(rgba(240, 147, 251, 0.8), rgba(245, 87, 108, 0.8)), url('/images/landingpage/FondoNuestra.png')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="text-center mb-8 sm:mb-12 lg:mb-16">
                        <div className="inline-block bg-yellow-100 text-yellow-800 px-3 py-2 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4">
                            🎯 NUESTRA MISIÓN
                        </div>
                        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-6 sm:mb-8 lg:mb-12 px-2">
                            Nuestra meta es 
                            <span className="text-yellow-600"> clara</span>
                        </h2>
                        
                        {/* Book Covers Row - Single Row */}
                        <div className="flex justify-center items-center gap-2 sm:gap-3 mb-8 sm:mb-12 lg:mb-16 overflow-x-auto overflow-y-hidden px-4">
                            {/* Book 1 */}
                            <div className="flex-shrink-0">
                                <div className="bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-110 border-2 border-yellow-200">
                                    <img 
                                        src="/images/portadas/portadalibro1.png" 
                                        alt="Libro 1" 
                                        className="w-16 h-20 sm:w-20 sm:h-24 object-cover rounded-md sm:rounded-lg"
                                    />
                                </div>
                            </div>

                            {/* Book 2 */}
                            <div className="flex-shrink-0">
                                <div className="bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-110 border-2 border-yellow-200">
                                    <img 
                                        src="/images/portadas/portadalibro2.png" 
                                        alt="Libro 2" 
                                        className="w-16 h-20 sm:w-20 sm:h-24 object-cover rounded-md sm:rounded-lg"
                                    />
                                </div>
                            </div>

                            {/* Book 3 */}
                            <div className="flex-shrink-0">
                                <div className="bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-110 border-2 border-yellow-200">
                                    <img 
                                        src="/images/portadas/portadalibro3.png" 
                                        alt="Libro 3" 
                                        className="w-16 h-20 sm:w-20 sm:h-24 object-cover rounded-md sm:rounded-lg"
                                    />
                                </div>
                            </div>

                            {/* Book 4 */}
                            <div className="flex-shrink-0">
                                <div className="bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-110 border-2 border-yellow-200">
                                    <img 
                                        src="/images/portadas/portadalibro4.png" 
                                        alt="Libro 4" 
                                        className="w-16 h-20 sm:w-20 sm:h-24 object-cover rounded-md sm:rounded-lg"
                                    />
                                </div>
                            </div>

                            {/* Book 5 */}
                            <div className="flex-shrink-0">
                                <div className="bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-110 border-2 border-yellow-200">
                                    <img 
                                        src="/images/portadas/portadalibro5.png" 
                                        alt="Libro 5" 
                                        className="w-16 h-20 sm:w-20 sm:h-24 object-cover rounded-md sm:rounded-lg"
                                    />
                                </div>
                            </div>

                            {/* Book 6 */}
                            <div className="flex-shrink-0">
                                <div className="bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-110 border-2 border-yellow-200">
                                    <img 
                                        src="/images/portadas/portadalibro6.png" 
                                        alt="Libro 6" 
                                        className="w-16 h-20 sm:w-20 sm:h-24 object-cover rounded-md sm:rounded-lg"
                                    />
                                </div>
                            </div>

                            {/* Book 7 */}
                            <div className="flex-shrink-0">
                                <div className="bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-110 border-2 border-yellow-200">
                                    <img 
                                        src="/images/portadas/portadalibro7.png" 
                                        alt="Libro 7" 
                                        className="w-16 h-20 sm:w-20 sm:h-24 object-cover rounded-md sm:rounded-lg"
                                    />
                                </div>
                            </div>

                            {/* Book 8 */}
                            <div className="flex-shrink-0">
                                <div className="bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-110 border-2 border-yellow-200">
                                    <img 
                                        src="/images/portadas/portadalibro8.png" 
                                        alt="Libro 8" 
                                        className="w-16 h-20 sm:w-20 sm:h-24 object-cover rounded-md sm:rounded-lg"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Two Column Content */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12">
                            {/* Left Column - Sabiduría */}
                            <div className="text-left">
                                <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-xl border-l-4 border-yellow-400">
                                    <div className="flex items-center mb-4 sm:mb-6">
                                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-yellow-400 rounded-full flex items-center justify-center mr-3 sm:mr-4">
                                            <span className="text-2xl sm:text-3xl">🧠</span>
                                        </div>
                                        <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800">
                                            Sabiduría
                                        </h3>
                                    </div>
                                    <div className="space-y-3 sm:space-y-4 text-gray-700">
                                        <p className="text-sm sm:text-base lg:text-lg leading-relaxed">
                                            Un niño puede demostrar mayor sabiduría que un adulto, ya que esta no tiene nada que ver con la edad o la experiencia.
                                        </p>
                                        <p className="text-sm sm:text-base lg:text-lg leading-relaxed">
                                            Garantizamos la obtención de sabiduría en tus hijos, lo que les permitirá ganar cualquier debate en casa y en la escuela.
                                        </p>
                                    </div>
                                    <div className="mt-4 sm:mt-6 bg-yellow-100 rounded-lg sm:rounded-xl p-3 sm:p-4">
                                        <h4 className="text-base sm:text-lg font-bold text-gray-800 mb-1 sm:mb-2">🏆 Resultados Tangibles</h4>
                                        <p className="text-sm sm:text-base text-gray-700">Cambio de hábitos positivos, títulos e insignias</p>
                                    </div>
                                </div>
                            </div>

                            {/* Right Column - Propósito */}
                            <div className="text-left">
                                <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-xl border-l-4 border-green-400">
                                    <div className="flex items-center mb-4 sm:mb-6">
                                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-400 rounded-full flex items-center justify-center mr-3 sm:mr-4">
                                            <span className="text-2xl sm:text-3xl">💰</span>
                                        </div>
                                        <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800">
                                            Propósito
                                        </h3>
                                    </div>
                                    <div className="space-y-3 sm:space-y-4 text-gray-700">
                                        <p className="text-sm sm:text-base lg:text-lg leading-relaxed">
                                            Tenemos como meta la formación financiera de tus hijos. Al final de la experiencia, recibirán financiamiento o guía para la obtención de recursos para un emprendimiento o una vocación profesional.
                                        </p>
                                    </div>
                                    <div className="mt-4 sm:mt-6 bg-green-100 rounded-lg sm:rounded-xl p-3 sm:p-4">
                                        <h4 className="text-base sm:text-lg font-bold text-gray-800 mb-1 sm:mb-2">🏆 Resultados Tangibles</h4>
                                        <p className="text-sm sm:text-base text-gray-700">Financiamiento, bonos económicos, emprendimiento real, motivación vocacional</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* CTA Button */}
                        <div className="text-center mt-8 sm:mt-12">
                            <CTAButton 
                                ctaName="Quiero que mis hijos inicien una experiencia transformadora"
                                section="Our Goal Section"
                                as="a"
                                href="https://www.amazon.com/dp/B0FX2BBP1D?ref_=pe_93986420_774957520"
                            >
                                Quiero que mis hijos inicien una experiencia transformadora
                            </CTAButton>
                            <div className="bg-black/60 backdrop-blur-sm rounded-lg px-4 py-2 mt-2 sm:mt-4 inline-block border border-white/20">
                                <p className="text-white text-xs sm:text-sm font-medium">
                                    🎯 8 libros únicos • 💡 Metodología probada • 🏆 Resultados garantizados
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Book Structure Section */}
            <section 
                className="py-12 sm:py-16 lg:py-20 relative"
                style={{
                    background: `linear-gradient(rgba(255,255,255,0.9), rgba(255,255,255,0.9)), url('/images/landingpage/FondoExperiencia.png')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="text-center mb-6 sm:mb-8">
                        <div className="inline-block bg-blue-100 text-blue-800 px-3 py-2 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4">
                            📚 METODOLOGÍA ÚNICA
                        </div>
                        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4 sm:mb-6 px-2">
                            Estructura de nuestros 
                            <span className="text-blue-600"> libros</span>
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12">
                        {/* Left Column - Book Cover and Activities */}
                        <div className="flex justify-center lg:justify-start">
                            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                                {/* Children Activities Illustration - Left Side */}
                                <div className="bg-white/90 backdrop-blur-sm rounded-xl sm:rounded-2xl p-2 sm:p-3 shadow-xl max-w-40 sm:max-w-48 transform hover:scale-105 transition-transform duration-300 order-2 sm:order-1">
                                    <img 
                                        src="/images/Actividades de bondad infantil.png" 
                                        alt="Niños realizando actividades de bondad" 
                                        className="w-full h-auto object-contain"
                                    />
                                </div>
                                
                                {/* Book Cover */}
                                <div className="relative order-1 sm:order-2">
                                    <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-2xl max-w-sm sm:max-w-lg transform hover:scale-105 transition-transform duration-300 border-2 sm:border-4 border-blue-200">
                                        <img 
                                            src="/images/portadas/portadalibro1.png" 
                                            alt="Libro 1 - Aprender a Aprender" 
                                            className="w-full h-auto object-contain"
                                        />
                                    </div>
                                    {/* Efecto decorativo */}
                                    <div className="absolute -top-2 -right-2 sm:-top-3 sm:-right-3 lg:-top-4 lg:-right-4 w-6 h-6 sm:w-8 sm:h-8 lg:w-12 lg:h-12 bg-blue-400 rounded-full flex items-center justify-center">
                                        <span className="text-white text-xs sm:text-sm lg:text-lg">📖</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Column - Content */}
                        <div className="space-y-4 sm:space-y-6 lg:space-y-8">
                            {/* Cuento Infantil */}
                            <div className="space-y-3 sm:space-y-4">
                                <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-xl border-l-4 border-yellow-400">
                                    <div className="flex items-center mb-3 sm:mb-4">
                                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-yellow-400 rounded-full flex items-center justify-center mr-3 sm:mr-4">
                                            <span className="text-xl sm:text-2xl">📖</span>
                                        </div>
                                        <h3 className="text-xl sm:text-2xl font-bold text-gray-800">
                                            Cuento Infantil
                                        </h3>
                                    </div>
                                    <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed">
                                        A través de un cuento infantil, transmitimos un mensaje reflexivo sobre el tema central del libro. En el primero, la historia de "El Nuevo Traje del Rey" enfatiza la importancia del conocimiento y la verdad.
                                    </p>
                                </div>
                            </div>

                            {/* Desarrollo Metacognitivo */}
                            <div className="space-y-3 sm:space-y-4">
                                <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-xl border-l-4 border-blue-400">
                                    <div className="flex items-center mb-3 sm:mb-4">
                                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-400 rounded-full flex items-center justify-center mr-3 sm:mr-4">
                                            <span className="text-xl sm:text-2xl">🧠</span>
                                        </div>
                                        <h3 className="text-xl sm:text-2xl font-bold text-gray-800">
                                            Desarrollo Metacognitivo
                                        </h3>
                                    </div>
                                    <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed">
                                        De forma didáctica, la segunda parte de los libros presenta teorías de desarrollo cognitivo. Esto se acompaña de ejercicios de reflexión y escritura que promueven la metacognición. En el primer libro, se enseñan los distintos tipos de conocimiento para que tus hijos aprendan a distinguir la verdad.
                                    </p>
                                </div>
                            </div>

                            {/* Experiencia Vivencial */}
                            <div className="space-y-3 sm:space-y-4">
                                <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-xl border-l-4 border-green-400">
                                    <div className="flex items-center mb-3 sm:mb-4">
                                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-400 rounded-full flex items-center justify-center mr-3 sm:mr-4">
                                            <span className="text-xl sm:text-2xl">🔬</span>
                                        </div>
                                        <h3 className="text-xl sm:text-2xl font-bold text-gray-800">
                                            Experiencia Vivencial
                                        </h3>
                                    </div>
                                    <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed">
                                        En la tercer parte de nuestros libros incluimos experimentos sociales y científicos que permiten a los niños vivir en primera persona los conceptos explicados en el libro.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* CTA Button */}
                    <div className="text-center mt-6 sm:mt-8">
                        <CTAButton 
                            ctaName="Quiero que mis hijos Aprendan a Aprender"
                            section="Book Structure Section"
                            as="a"
                            href="https://www.amazon.com/dp/B0FX2BBP1D?ref_=pe_93986420_774957520"
                        >
                            Quiero que mis hijos Aprendan a Aprender
                        </CTAButton>
                        <div className="mt-2 sm:mt-4">
                            <div className="bg-black/60 backdrop-blur-sm rounded-lg px-4 py-2 inline-block border border-white/20">
                                <p className="text-white text-xs sm:text-sm font-medium">
                                    🎯 Metodología única • 📖 Cuentos clásicos • 🔬 Experimentos prácticos
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            </main>
            
            <footer className="bg-gray-900 text-white py-8 sm:py-12 relative">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 sm:gap-8 mb-6 sm:mb-8">
                        <div className="md:col-span-2">
                            <div className="flex items-center mb-4 sm:mb-6">
                                <img 
                                    src="/images/princonser kids logo.png" 
                                    alt="Princonser Kids Logo" 
                                    className="h-10 sm:h-12 w-auto mr-3 sm:mr-4"
                                />
                                <div>
                                    <h3 className="text-lg sm:text-xl font-bold text-white">Princonser Kids</h3>
                                    <p className="text-yellow-300 text-xs sm:text-sm">Educación Integral</p>
                                </div>
                            </div>
                            <p className="text-gray-300 leading-relaxed mb-4 sm:mb-6 text-sm sm:text-base">
                                Transformando la educación infantil con sabiduría y tecnología. La única metodología que combina cuentos clásicos, desarrollo cognitivo y formación financiera para niños de 7-12 años.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                                <div className="bg-yellow-400 text-black px-3 py-2 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-bold">
                                    🏆 Avalado Internacionalmente
                                </div>
                                <div className="bg-green-400 text-black px-3 py-2 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-bold">
                                    📚 8 Libros Únicos
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="border-t border-gray-700 pt-6 sm:pt-8">
                        <div className="flex flex-col md:flex-row justify-between items-center">
                            <div className="text-gray-400 text-xs sm:text-sm mb-3 md:mb-0">
                                <p>&copy; 2024 Princonser Kids. Todos los derechos reservados.</p>
                                <p className="mt-1">Transformando el futuro de la educación infantil.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingPage;